import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../../utils/ToastSettings";
import { SectionsService } from "../../../services/Articles/Sections/SectionsService";
import { SectionsModel } from "../../../models/Articles/Sections/SectionsModel";
import { useLocation } from "react-router-dom";

interface ContextProps {
  sections: SectionsModel[];
  sectionsOptions: any[];
  setSectionsOptions: Dispatch<SetStateAction<any[]>>;
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  isLoadingModalSection: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handleListSections: () => void;
  handleFetchSection: (data: any) => Promise<boolean>;
  handleDeleteSection: (id: number) => Promise<boolean>;
  handleReactivate: (id: number) => Promise<boolean>;
  handleListSectionsOption: (filter: any) => void;
  handleSortSections: (sections: any) => Promise<boolean>;
}

export const SectionsContext = createContext<ContextProps>({} as ContextProps);

interface SectionsProviderProps {
  children: ReactNode;
}

export const SectionsProvider = ({ children }: SectionsProviderProps) => {
  const [sections, setSections] = useState<SectionsModel[]>([]);
  const [sectionsOptions, setSectionsOptions] = useState<any | null>(null);
  const [params, setParams] = useState<any | null>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingModalSection, setIsLoadingModalSection] =
    useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const service = new SectionsService();
  const location = useLocation();

  const handleListSections = useCallback(async () => {
    if (
      !params ||
      (!!params?.withPaginate &&
        !location.pathname.includes("/articles/sections/list"))
    ) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setSections(_Response?.items);
      setPages(_Response?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setSections([]);
      setPages({});
      setError("Não foi possível carregar as seções.");
    }
  }, [params, location.pathname]);

  const handleListSectionsOption = useCallback(async (filter: any) => {
    try {
      setIsLoadingModalSection(true);
      const [_Response, _Error] = await service.list(filter);
      setIsLoadingModalSection(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setSectionsOptions(
        _Response?.items.map((item: SectionsModel) => ({
          label: item.name,
          value: item.id,
        }))
      );
      return setError("");
    } catch (e) {
      setIsLoadingModalSection(false);
      setSectionsOptions([]);
      setError("Não foi possível carregar as seções.");
    }
  }, []);

  const handleFetchSection = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "top-right",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar a seções");
      return false;
    }
  };

  const handleDeleteSection = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.delete(id);
      setIsLoading(false);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao deletar a seções"
      );
      return false;
    }
  };

  const handleReactivate = async (id: number) => {
    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.reactivate(id);

      setTimeout(() => {
        setIsLoading(false);
      }, 1000);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoading(false);
      setError(
        e.response?.data?.message || "Houve um erro ao reativar a seções"
      );
      return false;
    }
  };

  const handleSortSections = async (sections: any) => {
    try {
      setIsLoadingModalSection(true);
      const [_Response, _Error] = await service.sort(sections);

      setTimeout(() => {
        setIsLoadingModalSection(false);
      }, 1000);

      if (!!_Error) {
        setError(_Response?.message || _Error);
        return false;
      }

      setError("");
      return true;
    } catch (e: any) {
      setIsLoadingModalSection(false);
      setError(e.response?.data?.message || "Houve um erro ao ordenar seções");
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleListSections().then();
  }, [handleListSections]);

  useEffect(() => {
    setSectionsOptions(
      sections?.map((item: SectionsModel) => ({
        label: item.name,
        value: item.id,
      }))
    );
  }, [sections]);

  return (
    <SectionsContext.Provider
      value={{
        sectionsOptions,
        setSectionsOptions,
        sections,
        params,
        setParams,
        pages,
        setPages,
        isLoading,
        isLoadingModalSection,
        error,
        setError,
        handleListSections,
        handleFetchSection,
        handleDeleteSection,
        handleReactivate,
        setIsLoading,
        handleListSectionsOption,
        handleSortSections,
      }}
    >
      {children}
    </SectionsContext.Provider>
  );
};

export const useSections = () => useContext(SectionsContext);
